import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
// import { Helmet } from 'react-helmet';
import { Users, MessageCircle, Heart } from 'lucide-react';
import { Button } from '../components/ui';
import { getUploadUrl } from '../utils/url';

interface LandingPageData {
  id: number;
  eventId: number;
  slug: string;
  metaDescription?: string;
  section1Title?: string;
  section1Content?: string;
  section2Title?: string;
  section2Content?: string;
  section3Title?: string;
  section3Content?: string;
  section4Title?: string;
  section4Content?: string;
  isPublished: boolean;
  event: {
    id: number;
    title: string;
    slug: string;
    startDate: string;
    location: string | null;
    image: string | null;
  };
}

interface LandingPageProps {
  slug: string;
}

const LandingPage: React.FC<LandingPageProps> = ({ slug }) => {
  const [landingPage, setLandingPage] = useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (slug) {
      loadLandingPage();
    }
  }, [slug]);

  // Set SEO meta tags using useEffect (moved before early returns to fix hooks error)
  useEffect(() => {
    if (landingPage) {
      const pageTitle = `Alleen naar ${landingPage.event.title}? Maak een groep met Solofix!`;
      const metaDescription = landingPage.metaDescription ||
        `Ga je alleen naar ${landingPage.event.title}? Vind je perfecte festivalgroep met Solofix. Ontmoet leuke mensen en maak er samen een onvergetelijke ervaring van!`;

      // Set page title
      document.title = pageTitle;

      // Helper function to set or update meta tag
      const setMetaTag = (name: string, content: string, property?: boolean) => {
        const attribute = property ? 'property' : 'name';
        const selector = `meta[${attribute}="${name}"]`;
        let meta = document.querySelector(selector) as HTMLMetaElement;

        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attribute, name);
          document.head.appendChild(meta);
        }
        meta.content = content;
      };

      // Set meta description
      setMetaTag('description', metaDescription);

      // Set Open Graph tags
      setMetaTag('og:title', pageTitle, true);
      setMetaTag('og:description', metaDescription, true);
      setMetaTag('og:type', 'website', true);
      setMetaTag('og:url', window.location.href, true);

      // Set event image if available
      const eventImageUrl = getEventImageUrl();
      if (eventImageUrl) {
        setMetaTag('og:image', eventImageUrl, true);
        setMetaTag('twitter:image', eventImageUrl);
      }

      // Set Twitter Card tags
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', pageTitle);
      setMetaTag('twitter:description', metaDescription);

      // Set canonical link
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = window.location.href;
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Solofix - Find your festival crew';
    };
  }, [landingPage]);

  const loadLandingPage = async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const response = await fetch(`/api/landing-pages/${slug}`);
      const result = await response.json();

      if (result.success) {
        setLandingPage(result.data);
      } else {
        setError(true);
      }
    } catch (error) {
      console.error('Error loading landing page:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getEventImageUrl = () => {
    if (!landingPage?.event.image) return null;
    return getUploadUrl(landingPage.event.image);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-pink mx-auto mb-4"></div>
          <p className="text-fg-muted">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !landingPage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold tracking-tight text-fg-bright mb-4">Pagina niet gevonden</h1>
          <p className="text-fg-muted mb-6">De pagina die je zoekt bestaat niet of is niet meer beschikbaar.</p>
          <Link to="/">
            <Button variant="primary">Ga naar homepage</Button>
          </Link>
        </div>
      </div>
    );
  }


  // Auto-scroll function
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          html, body {
            overflow-x: hidden !important;
            max-width: 100vw !important;
            box-sizing: border-box;
          }
          *, *::before, *::after {
            box-sizing: border-box;
          }
        `
      }} />
      <div className="min-h-screen overflow-x-hidden max-w-full w-full">
      {/* Hero Section — eventafbeelding met gradient-overlay */}
      <div className="relative overflow-hidden w-full">
        <div className="absolute inset-0">
          {getEventImageUrl() && (
            <div
              className="absolute inset-0 bg-cover bg-center opacity-40"
              style={{ backgroundImage: `url(${getEventImageUrl()})` }}
            />
          )}
          {/* Gradient-overlay naar de donkere canvas */}
          <div className="absolute inset-0 bg-gradient-to-b from-canvas/60 via-canvas/85 to-canvas"></div>
          {/* Neon gloed */}
          <div className="absolute -top-32 left-[15%] w-96 h-96 rounded-full bg-neon-violet/20 blur-3xl pointer-events-none"></div>
          <div className="absolute top-1/3 right-[5%] w-80 h-80 rounded-full bg-neon-pink/15 blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-[40%] w-72 h-72 rounded-full bg-neon-cyan/10 blur-3xl pointer-events-none"></div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[90vh] px-4 sm:px-6 lg:px-8 pt-6 sm:pt-0 pb-8 sm:pb-0">
            {/* Left: Content */}
            <div className="space-y-6 lg:pr-8">
              {/* Logo */}
              <div className="mb-6 sm:mb-8 px-1 flex items-center gap-2.5">
                <span className="sf-logo-mark h-[34px] w-[34px] text-[19px]">S</span>
                <span className="text-xl font-extrabold tracking-tight text-fg-bright">SOLOFIX</span>
              </div>

              <div className="sf-eyebrow">// vind je crew</div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-fg-bright leading-[1.05]">
                Alleen naar<br />
                <span className="sf-gradient-text">
                  {landingPage.event.title}?
                </span>
              </h1>

              <p className="text-lg text-fg-muted leading-relaxed">
                Ontdek je perfecte festivalgroep en maak er samen een onvergetelijke ervaring van
              </p>

              <div className="flex flex-col sm:flex-row gap-4 text-lg">
                {landingPage.event.location && (
                  <div className="flex items-center text-fg-body">
                    <svg className="w-5 h-5 mr-2 text-neon-pink" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {landingPage.event.location}
                  </div>
                )}
                <div className="flex items-center text-fg-body">
                  <svg className="w-5 h-5 mr-2 text-neon-cyan" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  {formatDate(landingPage.event.startDate)}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto animate-neon-pulse">
                    🚀 Start nu gratis
                  </Button>
                </Link>
                <Button
                  onClick={() => scrollToSection('section1')}
                  size="lg"
                  variant="secondary"
                  className="w-full sm:w-auto"
                >
                  Bekijk hoe het werkt
                </Button>
              </div>
            </div>

            {/* Right: Group Conversation Card */}
            <div className="relative lg:h-[500px] h-[350px] sm:h-[400px] px-4 sm:px-0">
              <div className="absolute -inset-2 bg-neon-gradient opacity-20 blur-2xl rounded-card pointer-events-none"></div>
              <div className="absolute inset-4 sm:inset-4 rounded-card border border-white/[.08] bg-surface shadow-panel overflow-hidden transform rotate-0 sm:rotate-3 hover:rotate-1 transition-transform duration-500">
                {/* Card Header */}
                <div className="border-b border-white/[.08] p-4" style={{ background: 'linear-gradient(90deg, rgba(255,61,129,.12), rgba(35,229,219,.08))' }}>
                  <div className="flex items-center space-x-3">
                    <div className="sf-dot-online animate-pulse"></div>
                    <h3 className="font-semibold text-fg-bright">Festivalgroep: {landingPage.event.title}</h3>
                    <span className="sf-badge-cyan">4 leden</span>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="p-2 sm:p-4 space-y-2 sm:space-y-3 h-full">
                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-avatar-pink rounded-full flex items-center justify-center text-white text-xs font-bold">M</div>
                    <div className="bg-white/[.06] border border-white/[.08] p-2 sm:p-3 rounded-[14px] flex-1">
                      <p className="text-xs sm:text-sm text-fg-soft">Hey allemaal! Super excited voor {landingPage.event.title}! 🎉</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-avatar-cyan rounded-full flex items-center justify-center text-ink text-xs font-bold">L</div>
                    <div className="bg-white/[.06] border border-white/[.08] p-2 sm:p-3 rounded-[14px] flex-1">
                      <p className="text-xs sm:text-sm text-fg-soft">Zelfde hier! Heb jullie al tickets? Kunnen we samen reizen?</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-avatar-violet rounded-full flex items-center justify-center text-white text-xs font-bold">S</div>
                    <div className="bg-white/[.06] border border-white/[.08] p-2 sm:p-3 rounded-[14px] flex-1">
                      <p className="text-xs sm:text-sm text-fg-soft">Ja! Ik heb een auto, kunnen met z'n allen 🚗</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-neon-gradient rounded-full flex items-center justify-center text-ink text-xs font-bold">D</div>
                    <div className="bg-bubble-own text-white p-2 sm:p-3 rounded-[14px] flex-1">
                      <p className="text-xs sm:text-sm">Perfect plan! Dit wordt zo vet samen! 🔥</p>
                    </div>
                  </div>

                  {/* Typing indicator */}
                  <div className="flex items-center space-x-2 text-fg-muted">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-fg-faint rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-fg-faint rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-fg-faint rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-xs">Emma is aan het typen...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-fg-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

      {/* Section 1: What is Solofix - Modern Card Layout */}
      {landingPage.section1Content && (
        <section id="section1" className="py-16 w-full overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div className="space-y-6">
                <div>
                  <div className="sf-eyebrow mb-3">// solofix</div>
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-fg-bright leading-tight">
                    {landingPage.section1Title || 'Wat is Solofix?'}
                  </h2>
                </div>

                <div className="text-base sm:text-lg text-fg-body leading-relaxed space-y-4" style={{ whiteSpace: 'pre-line' }}>
                  {landingPage.section1Content}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/register" className="w-full sm:w-auto">
                    <Button size="lg" className="w-full sm:w-auto">
                      Meld je aan voor Solofix
                    </Button>
                  </Link>
                  <Button
                    onClick={() => scrollToSection('section4')}
                    size="lg"
                    variant="secondary"
                    className="w-full sm:w-auto"
                  >
                    Leer meer
                  </Button>
                </div>
              </div>

              {/* Visual Element */}
              <div className="relative">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="sf-card p-4 sm:p-6 transition-colors duration-300 hover:bg-white/[.05]">
                    <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-avatar-pink mb-3 sm:mb-4">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-fg-bright mb-1 sm:mb-2 text-sm sm:text-base">Vind je crew</h3>
                    <p className="text-fg-muted text-xs sm:text-sm">Connect met gelijkgestemde festivalgangers</p>
                  </div>
                  <div className="sf-card p-4 sm:p-6 transition-colors duration-300 hover:bg-white/[.05]">
                    <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-avatar-cyan mb-3 sm:mb-4">
                      <MessageCircle className="w-5 h-5 text-ink" />
                    </div>
                    <h3 className="font-semibold text-fg-bright mb-1 sm:mb-2 text-sm sm:text-base">Chat & Plan</h3>
                    <p className="text-fg-muted text-xs sm:text-sm">Organiseer samen jullie festival avontuur</p>
                  </div>
                  <div className="sf-card p-4 sm:p-6 transition-colors duration-300 hover:bg-white/[.05]">
                    <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-avatar-violet mb-3 sm:mb-4">
                      <Heart className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-fg-bright mb-1 sm:mb-2 text-sm sm:text-base">Maak herinneringen</h3>
                    <p className="text-fg-muted text-xs sm:text-sm">Beleef samen onvergetelijke momenten</p>
                  </div>
                  <div className="sf-card p-4 sm:p-6 transition-colors duration-300 hover:bg-white/[.05]">
                    <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-neon-gradient mb-3 sm:mb-4">
                      <svg className="w-5 h-5 text-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-fg-bright mb-1 sm:mb-2 text-sm sm:text-base">100% Veilig</h3>
                    <p className="text-fg-muted text-xs sm:text-sm">Geverifieerde gebruikers en veilige chats</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section 2: About the Event - Full Width with Background */}
      {landingPage.section2Content && (
        <section id="section2" className="relative py-12 sm:py-16 lg:py-20 overflow-hidden w-full">
          {/* Background Image */}
          {getEventImageUrl() && (
            <div className="absolute inset-0">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${getEventImageUrl()})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-canvas/90 via-canvas/70 to-canvas/90"></div>
            </div>
          )}

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="sf-eyebrow mb-3">// het event</div>
            <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold tracking-tight text-fg-bright mb-6 leading-tight">
              {landingPage.section2Title || `Over ${landingPage.event.title}`}
            </h2>

            <div className="max-w-3xl mx-auto text-base sm:text-lg lg:text-xl text-fg-body leading-relaxed space-y-4 sm:space-y-6" style={{ whiteSpace: 'pre-line' }}>
              {landingPage.section2Content}
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-20 left-4 w-12 h-12 sm:w-20 sm:h-20 bg-neon-pink/20 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-4 w-16 h-16 sm:w-32 sm:h-32 bg-neon-violet/20 rounded-full blur-xl"></div>
        </section>
      )}

      {/* Section 3: How Solofix Works - Text Only */}
      {landingPage.section3Content && (
        <section className="py-12 sm:py-16 w-full overflow-x-hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Featured image at top */}
            {getEventImageUrl() && (
              <div className="flex justify-center mb-8">
                <div className="relative w-full max-w-2xl">
                  <img
                    src={getEventImageUrl() || ''}
                    alt={landingPage.event.title}
                    className="w-full h-64 object-cover rounded-card border border-white/[.08] shadow-panel"
                    style={{ aspectRatio: '4/2' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-canvas/50 to-transparent rounded-card"></div>
                </div>
              </div>
            )}

            <div className="sf-eyebrow mb-3">// zo werkt het</div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-fg-bright mb-6 leading-tight">
              {landingPage.section3Title || `Zo werkt Solofix voor ${landingPage.event.title}`}
            </h2>

            <div className="text-base sm:text-lg text-fg-body leading-relaxed mb-8" style={{ whiteSpace: 'pre-line' }}>
              {landingPage.section3Content}
            </div>

            <Link to="/register" className="inline-block w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto">
                Start je festival avontuur
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Section 4: Final Call to Action - Premium Design */}
      {landingPage.section4Content && (
        <section id="section4" className="relative py-12 sm:py-16 lg:py-20 overflow-hidden w-full">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-4 w-40 h-40 sm:w-72 sm:h-72 bg-neon-pink/15 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-4 w-48 h-48 sm:w-96 sm:h-96 bg-neon-violet/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 sm:w-64 sm:h-64 bg-neon-cyan/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="sf-eyebrow mb-3">// let's go</div>
            <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold tracking-tight text-fg-bright mb-6 leading-tight">
              {landingPage.section4Title || 'Klaar om je festivalgroep te vinden?'}
            </h2>

            <div className="max-w-3xl mx-auto text-base sm:text-lg lg:text-xl text-fg-body leading-relaxed mb-12 space-y-6" style={{ whiteSpace: 'pre-line' }}>
              {landingPage.section4Content}
            </div>

            {/* Call to Action Button */}
            <div className="flex justify-center mb-8">
              <Link to="/register" className="inline-block w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto animate-neon-pulse"
                >
                  🚀 Start nu gratis
                </Button>
              </Link>
            </div>

            {/* Trust Signals */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-fg-muted">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-neon-lime mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                100% gratis aanmelden
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-neon-lime mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Bepaal zelf je groepgenoten
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-neon-lime mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Voor elk evenement
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Modern Footer */}
      <footer className="border-t border-white/[.06] bg-white/[.02] py-12 w-full overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-lg sm:text-xl font-bold tracking-tight text-fg-bright mb-3">
                Ga niet alleen naar {landingPage.event.title}
              </h3>
              <p className="text-sm sm:text-base text-fg-muted mb-6 leading-relaxed">
                Duizenden mensen gebruiken Solofix om hun perfecte festivalgroep te vinden.
                Sluit je aan bij de community en maak nieuwe vrienden voor het leven.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto px-6 py-3"
                  >
                    Wordt lid van Solofix
                  </Button>
                </Link>
                <Link
                  to="/"
                  className="text-fg-dim hover:text-fg-muted transition-colors flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Terug naar Solofix
                </Link>
              </div>
            </div>

            <div className="text-center md:text-right">
              <div className="flex items-center gap-2.5 justify-center md:justify-end mb-4">
                <span className="sf-logo-mark h-[34px] w-[34px] text-[19px]">S</span>
                <span className="text-xl font-extrabold tracking-tight text-fg-bright">SOLOFIX</span>
              </div>
              <p className="text-fg-dim">Find your festival crew</p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-white/[.06]">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-fg-dim text-center sm:text-left">
                &copy; {new Date().getFullYear()} Solofix. Alle rechten voorbehouden.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 text-center sm:text-right">
                <Link
                  to="/privacy-statement"
                  className="text-fg-dim hover:text-fg-muted transition-colors text-sm"
                >
                  Privacy Statement
                </Link>
                <Link
                  to="/algemene-voorwaarden"
                  className="text-fg-dim hover:text-fg-muted transition-colors text-sm"
                >
                  Algemene Voorwaarden
                </Link>
                <div className="text-fg-dim text-sm">
                  KvK: 84894814
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
